import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../../css/items.scss";
import englishPlacement from "../../data/testdata/english-placement.json";
import ieltsMock from "../../data/testdata/ielts-mock.json";
import satMock from "../../data/testdata/sat-mock.json";
import interestProfiler from "../../data/testdata/interest-profiler.json";
import EnglishPlacementTest from "./EnglishPlacementTest";
import IeltsMockTest from "./IeltsMockTest";
import InterestProfilerTest from "./InterestProfilerTest";
import "./tests.scss";

const testDataById = {
  "interest-profiler": interestProfiler,
  "ielts-mock": ieltsMock,
  "sat-mock": satMock,
  "english-placement": englishPlacement,
};

const fallbackData = {
  backToTests: "Back to tests",
  notFoundTitle: "Test not found",
  notFoundMessage: "The test you are looking for is not available.",
  results: "Results",
  topInterests: "Your top interests",
  match: "match",
  minuteShort: "min",
  questionProgress: "Question {current} of {total}",
  questionsSoon: "Questions will be added soon.",
  chooseAnswer: "Please choose an answer before continuing.",
  answerAllQuestions: "Please answer every question before submitting.",
  answerAllInSection: "Answer every question in this section before continuing.",
  answerAllInSection: "Answer every question in this section before continuing.",
  previous: "Previous",
  next: "Next",
  submit: "Submit",
  items: {},
};

const formatText = (template, values) => {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value),
    template
  );
};

const TestTake = ({ data = fallbackData, language = "en" }) => {
  const { id } = useParams();
  const test = testDataById[id];
  const text = { ...fallbackData, ...data };
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const questions = useMemo(() => {
    if (!test) return [];
    return test.sections.flatMap((section) => section.questions || []);
  }, [test]);

  const getQuestionOptions = (question) => {
    return question.options || test.options || [];
  };

  const getOptionValue = (option) => {
    return typeof option === "object" ? option.value : option;
  };

  const getOptionLabel = (option) => {
    if (typeof option !== "object") return option;
    const label = text.answerLabels?.[option.value] || option.label;
    return option.emoji ? `${option.emoji} ${label}` : label;
  };

  const isSingleQuestionMode = test?.displayMode === "single-question";
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleAnswerChange = (questionId, value) => {
    const numericValue = Number(value);

    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: Number.isNaN(numericValue) ? value : numericValue,
    }));
    setResults(null);
    setError("");
  };

  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex((index) => Math.max(index - 1, 0));
    setError("");
  };

  const goToNextQuestion = () => {
    if (!currentQuestion || answers[currentQuestion.id] === undefined) {
      setError(text.chooseAnswer);
      return;
    }

    setCurrentQuestionIndex((index) => Math.min(index + 1, questions.length - 1));
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderQuestion = (question, index) => (
    <fieldset key={question.id} className="test-question">
      <legend>{index + 1}. {question.prompt}</legend>
      <div className={`test-question__options ${question.type === "emoji-scale" ? "test-question__options--emoji" : ""}`}>
        {getQuestionOptions(question).map((option) => (
          <label
            key={getOptionValue(option)}
            className={answers[question.id] === getOptionValue(option) ? "test-question__option--selected" : ""}
          >
            <input
              type="radio"
              name={question.id}
              value={getOptionValue(option)}
              checked={answers[question.id] === getOptionValue(option)}
              onChange={(event) => handleAnswerChange(question.id, event.target.value)}
            />
            <span>{getOptionLabel(option)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );

  const calculateResults = () => {
    if (!test.riasec) return null;

    const unanswered = questions.filter((question) => answers[question.id] === undefined);
    if (unanswered.length > 0) {
      setError(text.answerAllQuestions);
      return null;
    }

    const initialScores = test.riasec.reduce((scores, interest) => ({
      ...scores,
      [interest.code]: {
        ...interest,
        score: 0,
        maxScore: 0,
      },
    }), {});

    questions.forEach((question) => {
      const weights = question.weights || (question.riasec ? { [question.riasec]: 1 } : {});

      Object.entries(weights).forEach(([code, weight]) => {
        if (!initialScores[code] || !weight) return;
        initialScores[code].score += answers[question.id] * weight;
        initialScores[code].maxScore += 5 * weight;
      });
    });

    const ranked = Object.values(initialScores)
      .map((interest) => ({
        ...interest,
        percent: interest.maxScore > 0
          ? Math.round((interest.score / interest.maxScore) * 100)
          : 0,
      }))
      .sort((a, b) => b.percent - a.percent);

    const secondScore = ranked[1]?.percent || 0;
    const topMatches = ranked[0].percent - secondScore <= 10
      ? ranked.slice(0, 2)
      : ranked.slice(0, 1);

    return { ranked, topMatches };
  };

  const handleSubmit = () => {
    const calculatedResults = calculateResults();
    if (!calculatedResults) return;

    setResults(calculatedResults);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!test) {
    return (
      <div className="items-container" id="tests">
        <section className="upper-section">
          <div className="upper-section-inner">
            <div className="title-container">
              <h2>{text.notFoundTitle}</h2>
            </div>
          </div>
        </section>
        <main className="main-container test-runner">
          <Link to="/tests" className="test-runner__back">{text.backToTests}</Link>
          <p className="test-runner__instructions">{text.notFoundMessage}</p>
        </main>
      </div>
    );
  }

  if (test.id === "interest-profiler") {
    return <InterestProfilerTest test={test} data={text} language={language} />;
  }

  if (test.id === "english-placement") {
    return <EnglishPlacementTest test={test} data={text} language={language} />;
  }

  if (test.id === "ielts-mock" || test.id === "sat-mock") {
    return <IeltsMockTest test={test} data={text} language={language} />;
  }

  const testText = {
    ...test,
    ...(text.items?.[test.id] || {}),
  };

  return (
    <div className="items-container" id="tests">
      <section className="upper-section">
        <div className="upper-section-inner">
          <div className="title-container">
            <h2>{testText.title}</h2>
          </div>
        </div>
      </section>

      <main className="main-container test-runner">
        <Link to="/tests" className="test-runner__back">{text.backToTests}</Link>
        <p className="test-runner__instructions">{test.instructions}</p>

        {results ? (
          <section className="test-results" aria-live="polite">
            <h3>{test.resultTitle || text.results}</h3>
            <div className="test-results__chart">
              {results.ranked.map((interest) => (
                <div key={interest.code} className="riasec-bar">
                  <div className="riasec-bar__label">
                    <span className="riasec-bar__graphic">{interest.graphic}</span>
                    <strong>{interest.code}</strong>
                    <span>{interest.name}</span>
                  </div>
                  <div className="riasec-bar__track">
                    <div
                      className="riasec-bar__fill"
                      style={{ width: `${interest.percent}%` }}
                    />
                  </div>
                  <span className="riasec-bar__score">{interest.percent}%</span>
                </div>
              ))}
            </div>

            <div className="test-results__recommendations">
              <h4>{text.topInterests}</h4>
              <div className="test-results__matches">
                {results.topMatches.map((interest) => (
                  <article key={interest.code} className="interest-match">
                    <div className="interest-match__heading">
                      <span>{interest.graphic}</span>
                      <strong>{interest.name}</strong>
                    </div>
                    <p>{interest.description}</p>
                    <div className="interest-match__tags">
                      {interest.interests.map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {!results && isSingleQuestionMode ? (
          <section className="test-section test-section--single">
            <div className="test-section__header">
              <h3>{formatText(text.questionProgress, { current: currentQuestionIndex + 1, total: questions.length })}</h3>
            </div>
            <div className="test-progress" aria-hidden="true">
              <div
                className="test-progress__bar"
                style={{ width: `${((currentQuestionIndex + 1) / Math.max(questions.length, 1)) * 100}%` }}
              />
            </div>
            {currentQuestion ? renderQuestion(currentQuestion, currentQuestionIndex) : (
              <p className="test-section__empty">{text.questionsSoon}</p>
            )}
          </section>
        ) : null}

        {!results && !isSingleQuestionMode ? test.sections.map((section) => (
          <section key={section.id} className="test-section">
            <div className="test-section__header">
              <h3>{section.title}</h3>
              {section.durationMinutes ? <span>{section.durationMinutes} {text.minuteShort}</span> : null}
            </div>

            {section.questions.length > 0 ? (
              section.questions.map((question, index) => renderQuestion(question, index))
            ) : (
              <p className="test-section__empty">{text.questionsSoon}</p>
            )}
          </section>
        )) : null}

        {!results && error ? <p className="test-runner__error">{error}</p> : null}
        {!results && isSingleQuestionMode ? (
          <div className="test-runner__nav">
            <button
              type="button"
              className="test-runner__secondary"
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              {text.previous}
            </button>
            {isLastQuestion ? (
              <button type="button" className="test-runner__submit" onClick={handleSubmit}>{text.submit}</button>
            ) : (
              <button type="button" className="test-runner__submit" onClick={goToNextQuestion}>{text.next}</button>
            )}
          </div>
        ) : null}
        {!results && !isSingleQuestionMode ? (
          <button type="button" className="test-runner__submit" onClick={handleSubmit}>{text.submit}</button>
        ) : null}
      </main>
    </div>
  );
};

export default TestTake;
